import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
  Alert,
  Chip,
  Button,
  CircularProgress,
  TextField,
  MenuItem,
  Divider,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  Stack,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
} from "@mui/icons-material";
import matchingService, { Match } from "../services/matchingService";
import MatchCard from "./MatchCard";

const PendingMatchesList: React.FC = () => {
  const [matches, setMatches] = useState<Match[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Filters
  const [statusTab, setStatusTab] = useState<string>("pending");
  const [matchType, setMatchType] = useState<string>("");
  const [minScore, setMinScore] = useState<string>("");
  const [maxScore, setMaxScore] = useState<string>("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const limit = 12;

  useEffect(() => {
    loadMatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusTab, matchType, minScore, maxScore, page]);

  const loadMatches = async () => {
    try {
      setLoading(true);
      setError(null);

      const filters: any = {
        page,
        limit,
        status: statusTab !== "all" ? statusTab : undefined,
      };

      if (matchType) filters.match_type = matchType;
      if (minScore) filters.min_score = parseFloat(minScore);
      if (maxScore) filters.max_score = parseFloat(maxScore);

      const response = await matchingService.listMatches(filters);

      setMatches(response.matches || []);
      setTotal(response.pagination.total);
      setTotalPages(response.pagination.pages);
    } catch (err: any) {
      setError(err.message || "Erro ao carregar matches");
      setMatches([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (matchId: number) => {
    try {
      setActionLoading(matchId);
      setError(null);
      await matchingService.approveMatch(matchId);
      setSuccess("Match aprovado com sucesso!");
      await loadMatches();
    } catch (err: any) {
      setError(err.message || "Erro ao aprovar match");
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (matchId: number) => {
    try {
      setActionLoading(matchId);
      setError(null);
      await matchingService.rejectMatch(matchId);
      setSuccess("Match rejeitado com sucesso!");
      await loadMatches();
    } catch (err: any) {
      setError(err.message || "Erro ao rejeitar match");
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (matchId: number) => {
    if (!window.confirm("Tem a certeza que deseja eliminar este match?")) {
      return;
    }

    try {
      setActionLoading(matchId);
      setError(null);
      await matchingService.deleteMatch(matchId);
      setSuccess("Match eliminado com sucesso!");
      await loadMatches();
    } catch (err: any) {
      setError(err.message || "Erro ao eliminar match");
    } finally {
      setActionLoading(null);
    }
  };

  const handleClearFilters = () => {
    setMatchType("");
    setMinScore("");
    setMaxScore("");
    setPage(1);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" component="h1">
          Matches
        </Typography>
        <Button
          startIcon={<RefreshIcon />}
          onClick={loadMatches}
          disabled={loading}
        >
          Atualizar
        </Button>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={statusTab}
          onChange={(_, value) => {
            setStatusTab(value);
            setPage(1);
          }}
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          <Tab label="Pendentes" value="pending" />
          <Tab label="Aprovados" value="approved" />
          <Tab label="Rejeitados" value="rejected" />
          <Tab label="Todos" value="all" />
        </Tabs>

        <Box p={2}>
          <Stack spacing={2}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(5, 1fr)",
                },
                gap: 2,
                alignItems: "center",
              }}
            >
              <FormControl fullWidth size="small">
                <InputLabel>Tipo de Match</InputLabel>
                <Select
                  value={matchType}
                  label="Tipo de Match"
                  onChange={(e) => {
                    setMatchType(e.target.value);
                    setPage(1);
                  }}
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="automatic">Automático</MenuItem>
                  <MenuItem value="suggested">Sugerido</MenuItem>
                  <MenuItem value="manual">Manual</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                size="small"
                label="Score Mínimo"
                type="number"
                value={minScore}
                onChange={(e) => {
                  setMinScore(e.target.value);
                  setPage(1);
                }}
                inputProps={{ min: 0, max: 100 }}
              />

              <TextField
                fullWidth
                size="small"
                label="Score Máximo"
                type="number"
                value={maxScore}
                onChange={(e) => {
                  setMaxScore(e.target.value);
                  setPage(1);
                }}
                inputProps={{ min: 0, max: 100 }}
              />

              <Button
                fullWidth
                startIcon={<FilterIcon />}
                onClick={handleClearFilters}
                disabled={!matchType && !minScore && !maxScore}
              >
                Limpar Filtros
              </Button>

              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="right"
              >
                {total} matches encontrados
              </Typography>
            </Box>
          </Stack>
        </Box>
      </Paper>

      {/* Matches Grid */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : matches.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h6" color="text.secondary">
            Nenhum match encontrado
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Tente ajustar os filtros ou executar o matching automático
          </Typography>
        </Paper>
      ) : (
        <>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "repeat(2, 1fr)",
                lg: "repeat(3, 1fr)",
              },
              gap: 2,
            }}
          >
            {matches.map((match) => (
              <Box key={match.id}>
                <MatchCard
                  match={match}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  onDelete={handleDelete}
                  loading={actionLoading === match.id}
                />
              </Box>
            ))}
          </Box>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={4}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
                size="large"
              />
            </Box>
          )}
        </>
      )}
    </Container>
  );
};

export default PendingMatchesList;
